import { number, string } from "prop-types";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import styled from "styled-components/macro";
import theme from "theme";

import { motion } from "motion/react";

import { colorIsLight } from "Utilities/Colors/colorUtils";

function ProgressBar({
  totalPledge,
  pledgedAmountGiven,
  unpledgedAmountGiven,
  goal,
  color,
}) {
  const pledgeGivenRef = useRef();
  const pledgeAmountRef = useRef();
  const pledgeGivenAmountRef = useRef();

  const [pledgeGivenHovered, setPledgeGivenHovered] = useState(false);
  const [unpledgedGivenIsHovered, setUnpledgedGivenIsHovered] = useState(false);
  const [totalPledgeHovered, setTotalPledgeHovered] = useState(false);

  const pledgeGiven =
    typeof pledgedAmountGiven === "number" ? pledgedAmountGiven : 0;
  const unpledgedGiven =
    typeof unpledgedAmountGiven === "number" ? unpledgedAmountGiven : 0;

  const getPledgeWidth = () => {
    if (pledgeGivenRef.current && pledgeAmountRef.current) {
      const pledgeWidth = pledgeGivenRef.current.offsetWidth;
      const amountWidth = pledgeAmountRef.current.offsetWidth;
      return pledgeWidth + amountWidth;
    }
    return 0;
  };

  const totalPledgeWidth = getPledgeWidth();
  const hasTotalPledge = Boolean(totalPledge && totalPledge > 0);
  const hasPledgesGiven = Boolean(pledgedAmountGiven && pledgeGiven > 0);
  const hasUnpledgedGiven = Boolean(unpledgedAmountGiven && unpledgedGiven > 0);

  const getPledgedGivenWidth = () => {
    if (goal > 0) {
      const ratio = totalPledge / goal;
      if (ratio > 1) {
        return "100%";
      }
      return `${(totalPledge / goal) * 100 || 0}%`;
    } else if (unpledgedGiven > 0) {
      const widthRatio = totalPledge / (unpledgedGiven + totalPledge);
      return `${widthRatio * 75}%`;
    } else {
      return "50%";
    }
  };
  const getPledgedFlex = () => {
    if (goal > 0) {
      if (unpledgedGiven + pledgeGiven > goal) {
        return pledgeGiven / (unpledgedGiven + pledgeGiven);
      }
      return pledgeGiven / goal;
    } else if (unpledgedGiven > 0) {
      const widthRatio = totalPledge / (unpledgedGiven + totalPledge);
      return widthRatio;
    } else {
      return 0.5;
    }
  };

  const getUnpledgedGivenWidth = () => {
    if (goal > 0) {
      return `${(unpledgedGiven / goal) * 100 || 0}%`;
    } else if (totalPledge > 0) {
      const widthRatio =
        (unpledgedGiven / (unpledgedGiven + totalPledge)) * 0.75;
      return `${widthRatio * 75}%`;
    } else {
      return "50%";
    }
  };

  const getUnpledgedFlex = () => {
    if (goal > 0) {
      if (unpledgedGiven + pledgeGiven > goal) {
        return unpledgedGiven / (unpledgedGiven + pledgeGiven);
      }
      return unpledgedGiven / goal;
    } else if (totalPledge > 0) {
      const widthRatio =
        (unpledgedGiven / (unpledgedGiven + totalPledge)) * 0.75;
      return widthRatio;
    } else {
      return 0.5;
    }
  };

  const getDisplayNumber = (val) => {
    // Round to integer
    const integer = Math.round(val);
    // Add commas
    return integer.toLocaleString("en-US");
  };

  const totalGiven = unpledgedAmountGiven
    ? unpledgedAmountGiven + pledgedAmountGiven
    : pledgedAmountGiven;

  return (
    <ProgressBarWrapper>
      {hasUnpledgedGiven && (
        <DetailWrapper>
          <Details $show={unpledgedGivenIsHovered}>
            ${getDisplayNumber(unpledgedGiven)} unpledged gifts collected
          </Details>
        </DetailWrapper>
      )}
      {hasTotalPledge && (
        <DetailWrapper>
          <Details $show={pledgeGivenHovered}>
            ${getDisplayNumber(pledgeGiven)} fulfilled in $
            {getDisplayNumber(totalPledge)} pledged
          </Details>
        </DetailWrapper>
      )}
      {goal > 0 && (
        <DetailWrapper>
          <Details
            side="right"
            $show={
              totalPledgeHovered &&
              !pledgeGivenHovered &&
              !unpledgedGivenIsHovered
            }
          >
            ${getDisplayNumber(totalGiven)} collected of $
            {getDisplayNumber(goal)} fundraising goal
          </Details>
        </DetailWrapper>
      )}

      <ProgressBars>
        {hasUnpledgedGiven && (
          <UnpledgedGiven
            $hasPledgesGiven={hasPledgesGiven}
            $flex={getUnpledgedFlex()}
            onMouseEnter={() => setUnpledgedGivenIsHovered(true)}
            onMouseLeave={() => setUnpledgedGivenIsHovered(false)}
            backgroundColor={color}
          >
            <Amount>{getDisplayNumber(unpledgedGiven)}</Amount>
          </UnpledgedGiven>
        )}

        {hasTotalPledge && (
          <PledgeWrapper
            $totalPledgeWidth={totalPledgeWidth}
            $percentWidth={getPledgedGivenWidth()}
            $flex={getPledgedFlex()}
            $hasUnpledgedGiven={unpledgedGiven > 0}
            $hasGivenPledges={hasPledgesGiven}
            onMouseEnter={() => setPledgeGivenHovered(true)}
            onMouseLeave={() => setPledgeGivenHovered(false)}
          >
            <PledgeBorder />
            {hasPledgesGiven ? (
              <PledgeGiven
                ref={pledgeGivenRef}
                $percentWidth={`${(pledgeGiven / totalPledge) * 100 || 0}%`}
                $amountWidth={`${
                  pledgeGivenAmountRef.current?.offsetWidth + 22 || 0
                }px`}
                $hasUnpledgedGiven={hasUnpledgedGiven}
                backgroundColor={color}
              >
                <Amount ref={pledgeGivenAmountRef}>
                  {getDisplayNumber(pledgeGiven)}
                </Amount>
              </PledgeGiven>
            ) : null}

            <PledgeAmount
              ref={pledgeAmountRef}
              onMouseEnter={() => setTotalPledgeHovered(true)}
              onMouseLeave={() => setTotalPledgeHovered(false)}
            >
              <Amount>
                {totalPledge > 0 ? getDisplayNumber(totalPledge) : ""}
              </Amount>
            </PledgeAmount>
          </PledgeWrapper>
        )}

        {Boolean(goal && goal > 0) && (
          <Goal
            onMouseEnter={() => setTotalPledgeHovered(true)}
            onMouseLeave={() => setTotalPledgeHovered(false)}
          >
            <Amount>{getDisplayNumber(goal)}</Amount>
          </Goal>
        )}
      </ProgressBars>
    </ProgressBarWrapper>
  );
}

export default ProgressBar;

ProgressBar.propTypes = {
  totalPledge: number,
  pledgedAmountGiven: number,
  unpledgedAmountGiven: number,
  goal: number,
  color: string,
};

const ProgressBarWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 2.5rem;
  background-color: hsl(0 0% 100% / 0.1);
  border-radius: 2.5rem;
  border: 1px solid ${theme.colors.textLight};
  padding: 1px 2px;
  margin-block-start: 1.5rem;
`;

const ProgressBars = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  width: calc(100% + 2px);
`;

const innerRadius = "9999px";

const UnpledgedGiven = styled.div`
  position: relative;
  background-color: ${({ backgroundColor }) => backgroundColor || "#f59d00"};
  border-radius: ${(props) =>
    props.$hasPledgesGiven ? `${innerRadius} 0 0 ${innerRadius}` : innerRadius};
  height: calc(100% - 2px);
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ backgroundColor }) =>
    colorIsLight(backgroundColor)
      ? theme.colors.textAlmostBlack
      : theme.colors.textLight};
  padding: 0 0.75rem;
  font-weight: bold;
  /* width: max(5%, ${(props) => props.width}); */
  flex: ${({ $flex }) => $flex};
  box-shadow: inset rgb(0 0 0 / 40%) 0 0 1px;
  overflow: hidden;
  z-index: 1;

  &:before {
    content: "";
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.25),
      rgba(255, 255, 255, 0)
    );
    z-index: 1;
  }
  &:after {
    content: "";
    position: absolute;
    height: 14px;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.25)
    );
    z-index: 1;
  }
`;

function getPledgeWrapperLeftValue(props) {
  if (props.$hasUnpledgedGiven && !props.$hasGivenPledges) {
    return "5px";
  }

  if (props.$hasUnpledgedGiven) return "-3px";

  return "0";
}

const PledgeWrapper = styled.div`
  position: relative;
  /* width: ${(props) =>
    `max(calc(${props.$totalPledgeWidth}px + 32px), ${props.$percentWidth})`}; */
  /* width: ${(props) => `calc(${props.$percentWidth} + 4px)`}; */
  height: 100%;
  display: grid;
  grid-template-areas: "pledge";
  padding: ${(props) => {
    if (!props.$hasUnpledgedGiven && props.$hasGivenPledges) {
      return "1px 2px 1px 2px";
    }
    if (props.$hasUnpledgedGiven && props.$hasGivenPledges) {
      return "1px 2px 1px 0px";
    }
    return "1px 2px";
  }};
  left: ${(props) => getPledgeWrapperLeftValue(props)};
  flex: ${({ $flex }) => $flex};
`;
const PledgeBorder = styled.div`
  position: absolute;
  top: -1px;
  left: 0;
  right: 2px;
  bottom: -1px;
  width: 100%;
  min-width: 40px;
  box-shadow: 0 0 0 4px ${theme.colors.textLight};
  border-radius: 40px;
  box-sizing: border-box;
  z-index: 3;
`;

const PledgeGiven = styled.div`
  position: relative;
  grid-area: pledge;
  background-color: ${({ backgroundColor }) => backgroundColor || "#f59d00"};
  border-radius: ${(props) =>
    props.$hasUnpledgedGiven ? `35px 40px 40px 35px` : innerRadius};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ backgroundColor }) =>
    colorIsLight(backgroundColor)
      ? theme.colors.textAlmostBlack
      : theme.colors.textLight};

  padding: 0 0.75rem;
  font-weight: bold;
  /* width: ${(props) =>
    `max(${props.$amountWidth}, ${props.$percentWidth})`}; */
  /* left: ${(props) => (props.$hasUnpledgedGiven ? "-2px" : "0")}; */
  overflow: hidden;
  z-index: 4;

  &:before {
    content: "";
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.25),
      rgba(255, 255, 255, 0)
    );
    z-index: 1;
  }
  &:after {
    content: "";
    position: absolute;
    height: 14px;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.25)
    );
    z-index: 1;
  }
`;

const PledgeAmount = styled.div`
  grid-area: pledge;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 0.75rem;
  z-index: 1;
`;

const Goal = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  flex: 1;
  margin: 0 0.75rem;
`;

const Amount = styled.div`
  &:before {
    content: "$";
  }
`;

const DetailWrapper = styled.div`
  position: absolute;
  top: -1.5rem;
  left: 0;
  width: 100%;
  height: 1.25rem;
  overflow: hidden;

  display: flex;
  color: ${theme.colors.textLight};
  font-weight: 400;
`;

const Details = styled(motion.div)`
  position: absolute;
  top: 0;
  ${(props) => (props.side === "right" ? "right" : "left")}: .75rem;
  font-size: 0.875rem;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  transform: ${(props) =>
    props.$show ? "translateY(-2px)" : "translateY(20px)"};
  transition: opacity 0.2s ease, transform 0.2s ease;
`;

const SettingsButton = styled(Link)`
  background: none;
  border: none;
  color: ${theme.colors.textLight};
  cursor: pointer;
  border-radius: 0.25rem;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  opacity: 0.7;
  transition: all 0.2s ease;
  rotate: 30deg;

  &:hover {
    background: ${theme.colors.textLight};
    color: ${theme.colors.textPrimary};
    opacity: 1;
  }
`;
